import './styles.scoped.css'
import {useEffect, useRef, useState} from 'react'
import {Box, Tabs, Tab, Paper, Typography, Avatar, Stack, Button} from '@mui/material'
import {ChevronRightRounded, DateRange, AccessTime} from '@mui/icons-material'
import {getColorFromName, getInitials, ignoreTimezone, parseError, formatTime} from '../../../../utils/helpers'
import {format} from 'date-fns'
import {useMobile} from '../../../../hooks'
import {dashboardApi} from '../../../../services'
import {useGlobalStates} from '../../../../contexts/GlobalStatesContext'
import {VisitCardsSkeleton} from '../DashboardSkeletons'

export default () => {
    const {mobile} = useMobile()
    const {setMessageBox, branchId, dashboardDate, dashboardRefresh} = useGlobalStates()
    const [items, setItems] = useState(null)
    const [skeleton, setSkeleton] = useState(true)
    const requestId = useRef(0)

    // A new day, branch or Refresh replaces the list with the skeleton.
    useEffect(() => {
        setSkeleton(true)
        setItems(null)
    }, [branchId, dashboardDate, dashboardRefresh])

    useEffect(() => {
        const timeout = setTimeout(() => {
            if (branchId && dashboardDate) {
                const id = ++requestId.current
                dashboardApi
                    .getTodaysScheduleOverview(branchId, {date: format(dashboardDate, 'yyyy-MM-dd'), page_size: 100})
                    .then(
                        ({
                            data: {
                                data: {visits},
                            },
                        }) => {
                            if (id !== requestId.current) return
                            // setItems(visits)
                            setSkeleton(false)
                        },
                    )
                    .catch(error => {
                        if (id !== requestId.current) return
                        const {title, description} = parseError(error)
                        if (description) {
                            setMessageBox({status: 'error', title, description})
                        }
                        setSkeleton(false)
                    })
            }
        }, 500)
        return () => clearTimeout(timeout)
    }, [branchId, dashboardDate, dashboardRefresh])

    return (
        <div className="container">
            {skeleton && <VisitCardsSkeleton mobile={mobile} times={2} badge={100} buttons={[170, 125]} mobileButtons={[2]} />}
            {items?.map((item, index) => (
                <div key={index}>
                    <br />
                    {mobile ? (
                        <div className="card">
                            <Typography variant="h8" color="text.primary">
                                {item?.service_name}
                            </Typography>
                            <br />
                            <br />
                            <div className="row-content">
                                <Typography variant="overline" color="text.primary" style={{width: 90}}>
                                    Scheduled:
                                </Typography>
                                <div className="datetime2">
                                    <div className="row-content">
                                        <Typography variant="body3" color="text.secondary">
                                            <DateRange fontSize="small" />
                                        </Typography>
                                        <Typography variant="body3" color="text.secondary">
                                            {format(new Date(ignoreTimezone(item?.date)), 'EEE, dd MMM yyyy')}
                                        </Typography>
                                    </div>
                                    <div className="row-content">
                                        <Typography variant="body3" color="text.secondary">
                                            <AccessTime fontSize="small" />
                                        </Typography>
                                        <Typography variant="body3" color="text.secondary">
                                            {formatTime(item?.start_time)} - {formatTime(item?.end_time)}
                                        </Typography>
                                    </div>
                                </div>
                            </div>
                            <br />
                            <div className="row-content">
                                <Typography variant="overline" color="text.primary" style={{width: 90}}>
                                    Report:
                                </Typography>
                                <div className="datetime2">
                                    <div className="row-content">
                                        <Typography variant="body3" color="text.secondary">
                                            <DateRange fontSize="small" />
                                        </Typography>
                                        <Typography variant="body3" color="text.secondary">
                                            {format(new Date(ignoreTimezone(item?.date)), 'EEE, dd MMM yyyy')}
                                        </Typography>
                                    </div>
                                    <div className="row-content">
                                        <Typography variant="body3" color="text.secondary">
                                            <AccessTime fontSize="small" />
                                        </Typography>
                                        <Typography variant="body3" color="text.secondary">
                                            {formatTime(item?.start_time)} - {formatTime(item?.end_time)}
                                        </Typography>
                                    </div>
                                    <Typography variant="overline" color="error">
                                        15 units over
                                    </Typography>
                                </div>
                            </div>
                            <br />
                            <Typography variant="caption" color="primary">
                                CLIENT
                            </Typography>
                            <div className="people-card">
                                {item?.client?.avatar_url ? (
                                    <Avatar src={item?.client?.avatar_url} style={{width: 40, height: 40}} />
                                ) : (
                                    <Avatar
                                        style={{
                                            backgroundColor: getColorFromName(item?.client?.name),
                                            width: 40,
                                            height: 40,
                                            fontSize: 20,
                                        }}>
                                        {getInitials(item?.client?.name)}
                                    </Avatar>
                                )}
                                <div className="people-profile-card">
                                    <Typography variant="h8" color="text.primary" className="name">
                                        {item?.client?.name}
                                    </Typography>
                                    <Typography variant="body3" color="text.secondary" className="name">
                                        {item?.client?.email}
                                    </Typography>
                                </div>
                            </div>
                            <br />
                            <Typography variant="caption" color="primary">
                                CAREGIVER
                            </Typography>
                            <div className="people-card">
                                {item?.caregiver?.avatar_url ? (
                                    <Avatar src={item?.caregiver?.avatar_url} style={{width: 40, height: 40}} />
                                ) : (
                                    <Avatar
                                        style={{
                                            backgroundColor: getColorFromName(item?.caregiver?.name),
                                            width: 40,
                                            height: 40,
                                            fontSize: 20,
                                        }}>
                                        {getInitials(item?.caregiver?.name)}
                                    </Avatar>
                                )}
                                <div className="people-profile-card">
                                    <Typography variant="h8" color="text.primary" className="name">
                                        {item?.caregiver?.name}
                                    </Typography>
                                    <Typography variant="body3" color="text.secondary" className="name">
                                        {item?.caregiver?.email}
                                    </Typography>
                                </div>
                            </div>
                            <br />
                            <div className="actions">
                                <Button variant="outlined" size="small" fullWidth>
                                    Generate Report
                                </Button>
                                <Button variant="contained" size="small" fullWidth>
                                    Request Claim
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <div className="card">
                            <div className="card-top">
                                <Typography variant="h8" color="text.primary">
                                    {item?.service_name}
                                </Typography>
                                <div className="card-top-right">
                                    <div className="scheduled">
                                        <Typography variant="overline" color="text.primary" style={{width: 90}}>
                                            Scheduled:
                                        </Typography>
                                        <div className="datetime">
                                            <div className="row-content">
                                                <Typography variant="body3" color="text.secondary">
                                                    <DateRange fontSize="small" />
                                                </Typography>
                                                <Typography variant="body3" color="text.secondary">
                                                    {format(new Date(ignoreTimezone(item?.date)), 'EEE, dd MMM yyyy')}
                                                </Typography>
                                            </div>
                                            <Typography variant="body3" color="text.secondary">
                                                |
                                            </Typography>
                                            <div className="row-content">
                                                <Typography variant="body3" color="text.secondary">
                                                    <AccessTime fontSize="small" />
                                                </Typography>
                                                <Typography variant="body3" color="text.secondary">
                                                    {formatTime(item?.start_time)} - {formatTime(item?.end_time)}
                                                </Typography>
                                            </div>
                                        </div>
                                        <div style={{width: 120}} />
                                    </div>
                                    <div className="scheduled">
                                        <Typography variant="overline" color="text.primary" style={{width: 90}}>
                                            Report:
                                        </Typography>
                                        <div className="datetime">
                                            <div className="row-content">
                                                <Typography variant="body3" color="text.secondary">
                                                    <DateRange fontSize="small" />
                                                </Typography>
                                                <Typography variant="body3" color="text.secondary">
                                                    {format(new Date(ignoreTimezone(item?.date)), 'EEE, dd MMM yyyy')}
                                                </Typography>
                                            </div>
                                            <Typography variant="body3" color="text.secondary">
                                                |
                                            </Typography>
                                            <div className="row-content">
                                                <Typography variant="body3" color="text.secondary">
                                                    <AccessTime fontSize="small" />
                                                </Typography>
                                                <Typography variant="body3" color="text.secondary">
                                                    {formatTime(item?.start_time)} - {formatTime(item?.end_time)}
                                                </Typography>
                                            </div>
                                        </div>
                                        <Typography variant="overline" color="error" style={{width: 120}}>
                                            15 Units Over
                                        </Typography>
                                    </div>
                                </div>
                            </div>
                            <div className="card-bottom">
                                <div className="people-container">
                                    <div>
                                        <Typography variant="caption" color="primary">
                                            CLIENT
                                        </Typography>
                                        <div className="people-card">
                                            {item?.client?.avatar_url ? (
                                                <Avatar src={item?.client?.avatar_url} style={{width: 40, height: 40}} />
                                            ) : (
                                                <Avatar
                                                    style={{
                                                        backgroundColor: getColorFromName(item?.client?.name),
                                                        width: 40,
                                                        height: 40,
                                                        fontSize: 20,
                                                    }}>
                                                    {getInitials(item?.client?.name)}
                                                </Avatar>
                                            )}
                                            <div className="people-profile-card">
                                                <Typography variant="h8" color="text.primary" className="name">
                                                    {item?.client?.name}
                                                </Typography>
                                                <Typography variant="body3" color="text.secondary" className="name">
                                                    {item?.client?.email}
                                                </Typography>
                                            </div>
                                        </div>
                                    </div>
                                    <div>
                                        <Typography variant="caption" color="primary">
                                            CAREGIVER
                                        </Typography>
                                        <div className="people-card">
                                            {item?.caregiver?.avatar_url ? (
                                                <Avatar src={item?.caregiver?.avatar_url} style={{width: 40, height: 40}} />
                                            ) : (
                                                <Avatar
                                                    style={{
                                                        backgroundColor: getColorFromName(item?.caregiver?.name),
                                                        width: 40,
                                                        height: 40,
                                                        fontSize: 20,
                                                    }}>
                                                    {getInitials(item?.caregiver?.name)}
                                                </Avatar>
                                            )}
                                            <div className="people-profile-card">
                                                <Typography variant="h8" color="text.primary" className="name">
                                                    {item?.caregiver?.name}
                                                </Typography>
                                                <Typography variant="body3" color="text.secondary" className="name">
                                                    {item?.caregiver?.email}
                                                </Typography>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="actions">
                                    <Button variant="outlined">Generate PDF Report</Button>
                                    <Button variant="contained">Request Claim</Button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            ))}
        </div>
    )
}
